import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import { ViewProps, getTokenValue, Token, styled, View, isWeb, useThemeName } from 'tamagui'

import { Image } from '../Image'
import { Icon } from '../Icon'
import { usePalette } from '../Palette/PaletteContext'

import { GuildPrimaryPhotoNew_guild$key } from './__generated__/GuildPrimaryPhotoNew_guild.graphql'

const GuildDefaultFrame = styled(View, {
  name: 'GuildDefaultPhoto',
  backgroundColor: '$background',

  borderWidth: 1,
  borderColor: '$borderColor',

  variants: {
    size: {
      '...size': (size, { tokens }) => {
        const sizeToken = tokens.size[size]

        if (
          typeof sizeToken === 'object' &&
          'val' in sizeToken &&
          sizeToken.val > 40
        ) {
          return {
            boxShadow: '0 0 0 3px var(--background)',
            borderRadius: '$4',
          }
        }

        return {
          boxShadow: '0 0 0 1px var(--background)',
          borderRadius: '$2',
        }
      },
    },
  } as const,

  defaultVariants: {
    size: '$5',
  },
} as const)

const GuildPrimaryPhotoFrame = styled(Image, {
  name: 'GuildPrimaryPhoto',
  backgroundColor: '$background',

  borderWidth: 1,
  borderColor: '$borderColor',

  variants: {
    size: {
      '...size': (size, { tokens }) => {
        const sizeToken = tokens.size[size]

        if (
          typeof sizeToken === 'object' &&
          'val' in sizeToken &&
          sizeToken.val > 40
        ) {
          return {
            boxShadow: '0 0 0 3px var(--background)',
            borderRadius: '$4',
          }
        }

        return {
          boxShadow: '0 0 0 1px var(--background)',
          borderRadius: '$2',
        }
      },
    },
  } as const,

  defaultVariants: {
    size: '$5',
  },
} as const)

export const GuildPrimaryPhoto = GuildPrimaryPhotoFrame.styleable<
  HTMLImageElement,
  {
    guild: GuildPrimaryPhotoNew_guild$key
    size: Token
  } & ViewProps
>(({ guild, size = '$5', ...props }, ref) => {
  const data = useFragment(
    graphql`
      fragment GuildPrimaryPhotoNew_guild on Guild
      @argumentDefinitions(
        imageSize: { type: "Int", defaultValue: 100 }
        dpr: { type: "Int", defaultValue: 3 }
      ) {
        name
        rowId

        primaryPhoto {
          transformUrl(
            transform: { w: $imageSize, h: $imageSize, dpr: $dpr, fit: "cover" }
          )
          colorPalette
        }
      }
    `,
    guild
  )

  const parsedSize = useMemo(() => {
    return getTokenValue(size, 'size')
  }, [size])

  const { palette } = usePalette()
  const themeName = useThemeName()
  const isDark = themeName.startsWith('dark')

  // When a palette gradient is active and the image has a white/transparent
  // background, use a contrasting background so the logo stays visible.
  const paletteBackground = useMemo(() => {
    if (!palette || !isWeb || !data.primaryPhoto?.colorPalette) return undefined
    const cp = data.primaryPhoto.colorPalette as { background?: { type: string } } | null
    const bgType = cp?.background?.type
    if (bgType === 'white' || bgType === 'transparent') {
      return isDark ? 'rgba(255, 255, 255, 0.9)' : undefined
    }
    if (bgType === 'black') {
      return !isDark ? 'rgba(0, 0, 0, 0.9)' : undefined
    }
    return undefined
  }, [palette, isDark, data.primaryPhoto?.colorPalette])

  if (!data.primaryPhoto?.transformUrl) {
    return (
      <GuildDefaultFrame
        {...props}
        ref={ref}
        size={size}
        width={parsedSize}
        height={parsedSize}
        alignItems='center'
        justifyContent='center'
      >
        <Icon
          name='home-filled'
          width={parsedSize / 2}
          height={parsedSize / 2}
        />
      </GuildDefaultFrame>
    )
  }

  return (
    <GuildPrimaryPhotoFrame
      {...(props as any)}
      alt={`Primary Photo for ${data.name}`}
      ref={ref}
      size={size}
      src={data.primaryPhoto.transformUrl}
      width={parsedSize}
      height={parsedSize}
      {...(paletteBackground ? { backgroundColor: paletteBackground } : {})}
    />
  )
})
